// SimulatorPresets.jsx
// Static, non-draggable preset layouts. Replaces the buggy react-grid-layout
// drag-drop system. Each preset is a plain CSS Grid arrangement using the
// same widget components — but positions are FIXED, so:
//  - No "widget fantasma" bug (results_snapshot appearing on top of others)
//  - No collisions, no `preventCollision` reverts
//  - Identical between browsers/screens/users
//  - Preference stored per user (localStorage first, backend follows)

import { useEffect, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { LayoutGrid, LayoutPanelLeft, Columns3, Sliders } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRESET_KEY = "evolutt_simulator_preset";
export const PRESETS = {
  full: { id: "full", label: "Vista Completa", icon: LayoutPanelLeft, desc: "2 colunas · todos os widgets" },
};
const VALID = new Set(Object.keys(PRESETS));

/**
 * useEffectiveLayout — resolves the layout the user should see from backend v2.
 * Accepts a view (business_type × regime combo) so users can have DIFFERENT
 * layouts per view (e.g. "outsourcing_contract" vs "perm").
 */
export function useEffectiveLayout(getAuthHeaders, view) {
  const [state, setState] = useState({ rows: null, mandatory_widget_ids: [], custom_widgets: [], allow_customization: true, allowed_views: ["outsourcing_contract", "outsourcing_green_receipts", "outsourcing_b2b", "perm"], source: "default", view: "outsourcing_contract" });
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!getAuthHeaders) return;
    axios.get(`${API}/simulator-layout`, { headers: getAuthHeaders(), params: view ? { view } : {} })
      .then((res) => setState({
        rows: res.data.rows || [],
        mandatory_widget_ids: res.data.mandatory_widget_ids || [],
        custom_widgets: res.data.custom_widgets || [],
        allow_customization: res.data.allow_customization !== false,
        allowed_views: res.data.allowed_views || ["outsourcing_contract", "outsourcing_green_receipts", "outsourcing_b2b", "perm"],
        source: res.data.source || "default",
        view: res.data.view || view || "outsourcing_contract",
      }))
      .catch(() => setState((s) => ({ ...s, rows: [] })));
  }, [getAuthHeaders, view, refreshKey]);

  return { ...state, refresh: () => setRefreshKey((k) => k + 1) };
}

/**
 * useSimulatorPreset — resolves the preset from backend (user → group → global),
 * falls back to localStorage for offline-first responsiveness, and syncs changes
 * to both stores.
 */
export function useSimulatorPreset(getAuthHeaders, defaultPreset = "full") {
  const [preset, setPresetState] = useState(() => {
    try {
      const stored = localStorage.getItem(PRESET_KEY);
      return stored && VALID.has(stored) ? stored : defaultPreset;
    } catch { return defaultPreset; }
  });

  // Fetch effective preset from backend on mount
  useEffect(() => {
    if (!getAuthHeaders) return;
    axios.get(`${API}/layout-preference`, { headers: getAuthHeaders() })
      .then((res) => {
        const p = res.data?.preset;
        if (p && VALID.has(p)) {
          setPresetState(p);
          try { localStorage.setItem(PRESET_KEY, p); } catch { /* ignore */ }
        }
      })
      .catch(() => { /* silent: keep localStorage value */ });
  }, [getAuthHeaders]);

  const setPreset = (v) => {
    if (!VALID.has(v)) return;
    setPresetState(v);
    try { localStorage.setItem(PRESET_KEY, v); } catch { /* ignore */ }
    if (getAuthHeaders) {
      axios.put(`${API}/layout-preference`, { preset: v }, { headers: getAuthHeaders() })
        .catch(() => { /* silent: change stays local */ });
    }
  };
  return [preset, setPreset];
}

// ============ PRESET SELECTOR (pill segmented control) ============
export function PresetSelector({ preset, onChange }) {
  return (
    <div className="inline-flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-full p-1" data-testid="preset-selector">
      {Object.values(PRESETS).map((p) => {
        const Icon = p.icon;
        const isActive = preset === p.id;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onChange(p.id)}
            title={p.desc}
            data-testid={`preset-btn-${p.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              isActive ? "bg-[var(--brand-primary)] text-white shadow-sm" : "text-slate-600 hover:bg-white"
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            <span>{p.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// Small wrapper card shell — mirrors the previous widget card styling
// (kept for reuse; SimulatorPresets uses inline JSX instead to avoid
// react/no-unstable-nested-components warnings)

/**
 * SimulatorPresets — renders one of the 3 fixed layouts.
 *
 * Props:
 *   preset: "full" | "compact" | "compare"
 *   contents: { widget_id -> JSX } (same map as before; false/null means widget not renderable)
 */
export default function SimulatorPresets({ preset, contents, isAdmin, customLayout }) {
  // If a custom row-based layout is provided (from group config or user override),
  // it takes precedence over all fixed presets.
  if (customLayout?.rows?.length) {
    return (
      <div className="space-y-3" data-testid="preset-custom">
        {customLayout.rows.map((row, rIdx) => (
          <div key={rIdx} className="grid grid-cols-12 gap-3" data-testid={`custom-row-${rIdx}`}>
            {row.widgets.map((w, wIdx) => {
              const node = contents?.[w.id];
              // Custom HTML widget support
              const isCustomWidget = w.id?.startsWith("custom_");
              const customDef = isCustomWidget ? (customLayout.custom_widgets || []).find((cw) => cw.id === w.id) : null;
              if (!node && !customDef) return null;
              return (
                <div
                  key={`${w.id}-${wIdx}`}
                  className="bg-white rounded-lg shadow-sm border border-border/40 overflow-hidden min-w-0"
                  style={{ gridColumn: `span ${w.w} / span ${w.w}` }}
                  data-testid={`custom-widget-${w.id}`}
                >
                  {customDef ? (
                    <div className="p-3">
                      <div className="text-xs font-semibold text-brand-700 uppercase tracking-wide mb-2">{customDef.name}</div>
                      {/* Custom widgets configurados por admins podem conter HTML.
                          Sanitizamos com DOMPurify para prevenir XSS (script/on* handlers). */}
                      <div
                        className="text-sm text-slate-700 prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(customDef.html_content || "", {
                          ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "a", "h1", "h2", "h3", "h4", "span", "div", "img", "table", "thead", "tbody", "tr", "td", "th"],
                          ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "class", "style"],
                          ALLOW_DATA_ATTR: false,
                        }) }}
                      />
                    </div>
                  ) : node}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  }

  // Helper — only renders the widget if the content is truthy
  const renderWidget = (id, extraClass = "") => {
    const node = contents?.[id];
    if (!node) return null;
    return (
      <div
        data-testid={`preset-widget-${id}`}
        className={`bg-white rounded-lg shadow-sm border border-border/40 overflow-hidden min-w-0 ${extraClass}`}
      >
        {node}
      </div>
    );
  };
  const R = renderWidget;

  if (preset === "compact") {
    // ============ VISTA COMPACTA ============
    // 6 widgets essenciais, sem scroll (fits ≈ 1080px viewport).
    // Layout: 12 cols; Dados full width, depois 3 cols abaixo.
    return (
      <div className="grid grid-cols-12 gap-3" data-testid="preset-compact">
        <div className="col-span-12">{R("form_consultant", "min-h-[130px]")}</div>
        <div className="col-span-3">{R("results_score", "min-h-[220px]")}</div>
        <div className="col-span-6">{R("form_project", "min-h-[220px]")}</div>
        <div className="col-span-3">{R("results_company", "min-h-[220px]")}</div>
        <div className="col-span-6">{R("form_benefits", "min-h-[240px]")}</div>
        <div className="col-span-6">{R("results_employee", "min-h-[240px]")}</div>
      </div>
    );
  }

  if (preset === "compare") {
    // ============ VISTA COMPARATIVA ============
    // Snapshot em topo full-width + 3 colunas paralelas (Perfil | Cálculo | Empresa)
    return (
      <div className="space-y-3" data-testid="preset-compare">
        {R("results_snapshot", "min-h-[110px]")}
        <div className="grid grid-cols-12 gap-3 items-start">
          {/* Coluna 1: Perfil & Proposta */}
          <div className="col-span-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {R("form_business_type", "min-h-[110px]")}
              {R("form_regime", "min-h-[110px]")}
            </div>
            {R("form_consultant", "min-h-[180px]")}
            {R("form_proposal", "min-h-[220px]")}
          </div>
          {/* Coluna 2: Negócio & Cálculo */}
          <div className="col-span-4 space-y-3">
            {R("form_project", "min-h-[260px]")}
            {R("results_employee", "min-h-[280px]")}
          </div>
          {/* Coluna 3: Score & Empresa */}
          <div className="col-span-4 space-y-3">
            {R("results_score", "min-h-[200px]")}
            {R("results_company", "min-h-[280px]")}
            {isAdmin && R("results_premium", "min-h-[100px]")}
          </div>
        </div>
        {R("form_benefits", "min-h-[220px]")}
      </div>
    );
  }

  // ============ VISTA COMPLETA (default) ============
  // Inspirado no layout que o user desenhou (imagem 1):
  //  - Topo: Dados | TipoNeg | TipoReg  (3 blocos, larguras 5/3/4)
  //  - Snapshot full-width (para não colidir com nada)
  //  - Meio: Proposta (esquerda estreita) | Negócio (larga)
  //  - Baixo: Benefícios | Detalhe Colaborador | Detalhe Empresa
  //  - Aside sticky com Score + Prémios(admin)
  return (
    <div className="grid grid-cols-12 gap-3" data-testid="preset-full">
      {/* Linha 1: Dados + Tipo Negócio + Tipo Regime */}
      <div className="col-span-5">{R("form_consultant", "min-h-[170px]")}</div>
      <div className="col-span-3">{R("form_business_type", "min-h-[100px]")}</div>
      <div className="col-span-4">{R("form_regime", "min-h-[100px]")}</div>

      {/* Linha 2: Snapshot 9-col + Score col-span-3 (row-span-2 para descer até à linha 3) */}
      <div className="col-span-9">{R("results_snapshot", "min-h-[110px]")}</div>
      <div className="col-span-3 row-span-2">{R("results_score", "min-h-[230px] h-full")}</div>

      {/* Linha 3: Proposta (esquerda) + Negócio (larga) */}
      <div className="col-span-4">{R("form_proposal", "min-h-[380px]")}</div>
      <div className="col-span-5">{R("form_project", "min-h-[380px]")}</div>

      {/* Linha 4: Prémios logo abaixo de Negócio (pedido do user) */}
      {isAdmin && (
        <div className="col-span-12">{R("results_premium", "min-h-[100px]")}</div>
      )}

      {/* Linha 5: Benefícios + Detalhe Colaborador + Detalhe Empresa */}
      <div className="col-span-4">{R("form_benefits", "min-h-[320px]")}</div>
      <div className="col-span-4">{R("results_employee", "min-h-[320px]")}</div>
      <div className="col-span-4">{R("results_company", "min-h-[320px]")}</div>

      {/* Partilhar em rodapé */}
      <div className="col-span-12">{R("actions_share", "min-h-[100px]")}</div>
    </div>
  );
}
